// Drawer de Administración · Contratistas (handoff §8): el mismo
// formulario para «Editar contratista · X» y «Agregar contratista».
// Nombre, Razón social, Especialidad, Correo de contacto y Teléfono sobre
// el Drawer común; pie Cancelar · Guardar/Agregar, deshabilitado hasta que
// haya algo válido que mandar. Editar envía sólo lo que cambió (PATCH
// parcial); agregar respeta la regla del wire «al menos un correo o un
// teléfono».
//
// Sin RUT ni «Dónde trabaja»: providers no tiene esas columnas y esta
// ronda no migra (se ocultan, no se simulan). «Desactivar» no va en el
// pie: el handoff lo pone en la fila de la tabla.

import { useState } from "react";
import type { ProviderListItem } from "@bitacorapro/api-types";
import { Drawer } from "../../../components/Drawer";
import { Field } from "../activos/form";
import {
  createBody,
  creadoToast,
  draftFrom,
  EMPTY_DRAFT,
  patchDiff,
  saveErrorMessage,
  type ContratistaDraft,
  type DraftField,
} from "./model";
import { useCrearContratista, usePatchContratista } from "./queries";

export type ContratistaDrawerMode = { kind: "editar"; item: ProviderListItem } | { kind: "nuevo" };

export type ContratistaDrawerProps = {
  mode: ContratistaDrawerMode;
  onClose: () => void;
  /** Mensaje del toast que muestra la pantalla una vez cerrado el drawer. */
  onSaved: (message: string) => void;
};

export function ContratistaDrawer({ mode, onClose, onSaved }: ContratistaDrawerProps) {
  const [draft, setDraft] = useState<ContratistaDraft>(
    mode.kind === "editar" ? draftFrom(mode.item) : EMPTY_DRAFT
  );
  const [touched, setTouched] = useState<Partial<Record<DraftField, true>>>({});
  const [error, setError] = useState<string | null>(null);
  const crear = useCrearContratista();
  const patch = usePatchContratista();
  const pending = crear.isPending || patch.isPending;

  const check = mode.kind === "editar" ? patchDiff(mode.item, draft) : createBody(draft);
  const canSave = !pending && check.body !== null;

  function set(field: DraftField, value: string) {
    setDraft((d) => ({ ...d, [field]: value }));
  }
  function touch(field: DraftField) {
    setTouched((t) => ({ ...t, [field]: true }));
  }
  // El error inline aparece al salir del campo, no mientras se escribe.
  function fieldError(field: DraftField): string | null {
    return touched[field] ? (check.errors[field] ?? null) : null;
  }

  async function save() {
    setError(null);
    try {
      if (mode.kind === "editar") {
        const body = patchDiff(mode.item, draft).body;
        if (!body) return;
        await patch.mutateAsync({ id: mode.item.id, body });
        onSaved("Contratista actualizado");
      } else {
        const body = createBody(draft).body;
        if (!body) return;
        const res = await crear.mutateAsync(body);
        onSaved(creadoToast(res.provider.shortName, res.created));
      }
      onClose();
    } catch (err) {
      setError(saveErrorMessage(err));
    }
  }

  const editing = mode.kind === "editar";
  const title = editing ? `Editar contratista · ${mode.item.shortName}` : "Agregar contratista";

  return (
    <Drawer
      title={title}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            title="Cerrar sin guardar cambios"
            disabled={pending}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            title={
              editing
                ? "Guardar los datos de este contratista"
                : "Agregar este contratista a la nómina"
            }
            disabled={!canSave}
            onClick={() => void save()}
          >
            {pending ? "Guardando…" : editing ? "Guardar" : "Agregar"}
          </button>
        </>
      }
    >
      {editing && (
        <p className="ag-muted ag-mb16">
          Las observaciones ya asignadas siguen con este contratista; solo cambian sus datos.
        </p>
      )}
      {error && (
        <p className="auth-error" role="alert">
          {error}
        </p>
      )}
      <Field label="Nombre" error={fieldError("shortName")}>
        {(ctl) => (
          <input
            {...ctl}
            className="input"
            value={draft.shortName}
            onChange={(e) => set("shortName", e.target.value)}
            onBlur={() => touch("shortName")}
            disabled={pending}
            maxLength={200}
          />
        )}
      </Field>
      <Field label="Razón social" error={fieldError("legalName")}>
        {(ctl) => (
          <input
            {...ctl}
            className="input"
            value={draft.legalName}
            onChange={(e) => set("legalName", e.target.value)}
            onBlur={() => touch("legalName")}
            disabled={pending}
            maxLength={200}
          />
        )}
      </Field>
      <Field label="Especialidad" error={fieldError("specialty")}>
        {(ctl) => (
          <input
            {...ctl}
            className="input"
            value={draft.specialty}
            onChange={(e) => set("specialty", e.target.value)}
            onBlur={() => touch("specialty")}
            disabled={pending}
            maxLength={200}
            placeholder={editing ? undefined : "Ej. Climatización"}
          />
        )}
      </Field>
      <Field
        label="Correo de contacto"
        hint="Recibe el enlace público de cada observación asignada."
        error={fieldError("contactEmail")}
      >
        {(ctl) => (
          <input
            {...ctl}
            type="email"
            className="input"
            value={draft.contactEmail}
            onChange={(e) => set("contactEmail", e.target.value)}
            onBlur={() => touch("contactEmail")}
            disabled={pending}
          />
        )}
      </Field>
      {/* Teléfono no está en el handoff (sólo Correo de contacto); va porque
          el wire exige «al menos un correo o un teléfono» y la nómina ya lo
          guarda. El hint dice esa regla al dar de alta. Sin placeholders
          propios: el único del handoff es «Ej. Climatización». */}
      <Field
        label="Teléfono"
        hint={editing ? undefined : "Se necesita al menos un correo o un teléfono."}
        error={fieldError("contactPhone")}
      >
        {(ctl) => (
          <input
            {...ctl}
            type="tel"
            className="input"
            value={draft.contactPhone}
            onChange={(e) => set("contactPhone", e.target.value)}
            onBlur={() => touch("contactPhone")}
            disabled={pending}
            maxLength={40}
          />
        )}
      </Field>
    </Drawer>
  );
}
